import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { shortLabel } from "./labels";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function SummaryDialog({ cross, officeId, onClose, onSaved }) {
  const [date, setDate] = useState(todayStr());
  const [offices, setOffices] = useState([]);
  const [office, setOffice] = useState(officeId || "");
  const [allUsers, setAllUsers] = useState([]);
  const [operatorId, setOperatorId] = useState("");
  const [sum, setSum] = useState(null);
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cross) return;
    api.get("/offices").then(({ data }) => setOffices(data.filter((o) => o.active !== false))).catch(() => {});
    api.get("/users/assignable").then(({ data }) => setAllUsers(data.filter((u) => u.role === "operatore"))).catch(() => {});
  }, [cross]);
  const operators = allUsers.filter((u) => !office || u.office_id === office);
  useEffect(() => { if (cross && operators.length && !operators.some((u) => u.id === operatorId)) setOperatorId(operators[0].id); }, [cross, operators, operatorId]);

  const load = useCallback(async () => {
    if (cross && !operatorId) return;
    setSum(null); setError("");
    try {
      const { data } = await api.get("/monitoring/summary", { params: { date, operator_id: operatorId || undefined, office_id: cross && office ? office : undefined } });
      setSum(data); setForm({ visits: data.visits, by_type: { ...data.by_type } });
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  }, [date, operatorId, cross, office]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!reason.trim()) { toast.error("Indica la motivazione della correzione"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/monitoring/summary", { date, operator_id: operatorId || undefined, office_id: cross && office ? office : undefined, visits: +form.visits,
        by_type: Object.fromEntries(Object.entries(form.by_type).map(([k, v]) => [k, +v])), reason, expected: { visits: sum.visits, by_type: sum.by_type } });
      setSum(data); setForm({ visits: data.visits, by_type: { ...data.by_type } }); setReason("");
      if (data.unchanged) toast.info("Nessuna variazione: i totali erano già questi"); else toast.success("Riepilogo salvato: contatori, dashboard, obiettivi e badge aggiornati");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
      if (e.response?.status === 409) load();
    } finally { setSaving(false); }
  };

  const setQty = (key, v) => setForm((f) => key === "visits" ? { ...f, visits: v } : { ...f, by_type: { ...f.by_type, [key]: v } });
  const dirty = sum && form && (+form.visits !== sum.visits || sum.types.some((t) => +form.by_type[t.code] !== sum.by_type[t.code]));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="summary-dialog">
        <DialogHeader><DialogTitle>Riepilogo giornaliero — totali di ingressi e contratti</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="block"><span className="text-xs text-slate-500">Giorno</span><Input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="summary-date" /></label>
          {cross && (
            <label className="block"><span className="text-xs text-slate-500">Sede</span>
              <select className="block h-9 rounded-md border border-border bg-white px-2" value={office} onChange={(e) => setOffice(e.target.value)} data-testid="summary-office-select">
                <option value="">Tutte le sedi</option>{offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></label>
          )}
          {cross && (
            <label className="block"><span className="text-xs text-slate-500">Operatore</span>
              <select className="block h-9 rounded-md border border-border bg-white px-2" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} data-testid="summary-operator">
                {operators.map((u) => <option key={u.id} value={u.id}>{u.full_name}{!office && u.office_id ? ` — ${offices.find((o) => o.id === u.office_id)?.name || ""}` : ""}</option>)}
              </select></label>
          )}
          {sum && <span className="text-xs text-slate-500" data-testid="summary-office">{sum.operator_name} · {sum.office_name}</span>}
        </div>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-danger" data-testid="summary-error">{error}</p>}
        {!sum && !error ? <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div> : sum && (
          <>
            {!sum.editable && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="summary-locked">{sum.not_editable_reason}</p>}
            <p className="text-xs text-slate-500">I valori inseriti sono i <b>totali della giornata</b> e <b>sostituiscono</b> quelli attuali (non si sommano alle registrazioni singole). Ogni registrazione conta una sola volta.</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <QtyField label="Ingressi" value={form.visits} current={sum.visits} disabled={!sum.editable} onChange={(v) => setQty("visits", v)} testid="summary-qty-visits" />
              {sum.types.map((t) => <QtyField key={t.code} label={shortLabel(t)} value={form.by_type[t.code] ?? 0} current={sum.by_type[t.code] ?? 0} disabled={!sum.editable} onChange={(v) => setQty(t.code, v)} testid={`summary-qty-${t.code}`} />)}
            </div>
            <label className="block text-sm"><span className="text-xs text-slate-500">Motivazione della correzione (obbligatoria)</span>
              <textarea className="mt-0.5 w-full rounded-md border border-border px-2 py-1.5 text-sm" rows={2} value={reason} disabled={!sum.editable} onChange={(e) => setReason(e.target.value)} data-testid="summary-reason" /></label>
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-600">Storico correzioni del giorno ({sum.history.length})</div>
              {sum.history.length === 0 ? <p className="text-xs text-slate-400" data-testid="summary-history-empty">Nessuna correzione.</p> : (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs" data-testid="summary-history">
                  {sum.history.map((h) => (
                    <li key={h.id} className="rounded border border-border px-2 py-1" data-testid={`summary-history-${h.id}`}>
                      <div className="flex justify-between text-slate-500"><span>{fmt(h.created_at)} · {h.actor}</span></div>
                      <div className="text-slate-700">{diffText(h.before, h.after, sum.types)}</div>
                      <div className="italic text-slate-500">Motivo: {h.reason}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="summary-cancel">Chiudi</Button>
          <Button onClick={save} disabled={!sum?.editable || !dirty || saving} data-testid="summary-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "SALVA RIEPILOGO GIORNALIERO"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QtyField({ label, value, current, disabled, onChange, testid }) {
  const changed = +value !== current;
  return (
    <label className="block text-sm"><span className="text-xs text-slate-500">{label}</span>
      <Input type="number" min={0} step={1} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))} className={`h-9 ${changed ? "border-brand" : ""}`} data-testid={testid} />
      <span className="text-[10px] text-slate-400">attuale: {current}</span></label>
  );
}

function diffText(before, after, types) {
  const parts = [];
  if (before.visits !== after.visits) parts.push(`Ingressi ${before.visits} → ${after.visits}`);
  for (const t of types) { const b = before.by_type?.[t.code] ?? 0, a = after.by_type?.[t.code] ?? 0; if (b !== a) parts.push(`${shortLabel(t)} ${b} → ${a}`); }
  return parts.join(" · ") || "—";
}
